import React, { useEffect, useMemo, useState } from 'react';
import {ThemeProvider, createGlobalStyle} from 'styled-components'
import RootRouter from './routes';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import './i18n';
import { useTranslation } from 'react-i18next';
import "@fontsource/inter/variable.css";
import theme from './themes'
import { useUi } from './context/ui.context';
import {ModalVariables, TopProgress, TopPanelNotifications} from './components'
import { authService, clientService, permissionService, quotasService, setClientToken } from './api';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useAuth } from './context/user.context';
import { usePermission } from './permissions';
import { useCookies } from 'react-cookie';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RouterContextProvider } from './context/routerContext';
import 'react-loading-skeleton/dist/skeleton.css'
import config from './config';
import keycloak from './keycloak'
import { queries } from '@testing-library/react';
import {Helmet} from "react-helmet";
import { useQueryParam, StringParam, NumberParam, ArrayParam } from 'use-query-params';
import {HeroUIProvider} from "@heroui/react";
import { setStoredRegion, toStoredRegion } from './utils/region';

const AUTH_DEBUG_STORAGE_KEY = 'ocp_auth_debug'
const authDebugParam = new URLSearchParams(window.location.search).get('auth_debug')

if (authDebugParam === '1') {
  localStorage.setItem(AUTH_DEBUG_STORAGE_KEY, '1')
}

if (authDebugParam === '0') {
  localStorage.removeItem(AUTH_DEBUG_STORAGE_KEY)
}

const isAuthDebugEnabled = () => localStorage.getItem(AUTH_DEBUG_STORAGE_KEY) === '1'

const isCurrentInviteRegion = (inviteRegion?: string, currentRegion?: string | null) => {
  if (!inviteRegion) {
    return false
  }

  if (inviteRegion.trim().toLowerCase() === 'dev') {
    return true
  }

  return toStoredRegion(inviteRegion) === toStoredRegion(currentRegion)
}

const persistInvitationRegion = (region: string, setCookie: (name: string, value: string) => void) => {
  setCookie('invite_region', region)

  if(!toStoredRegion(region)){
    return
  }

  const previousRegion = localStorage.getItem('region_ocp_zone')
  const nextRegion = setStoredRegion(region)

  if(previousRegion !== nextRegion){
    window.location.reload()
  }
}

const GlobalStyle = createGlobalStyle<any>`
  body{
    background-color: ${({theme}) => theme.base.white};
    overflow-y: scroll;
    overflow-x: hidden !important;
    ::-webkit-scrollbar {
      width: 8px;
      height:2px;
    }
    ::-webkit-scrollbar-thumb {
      box-shadow: inset 0 0 2px 2px ${({theme}) => theme.isDark ? theme.palette[200] : theme.grey[400]};
      border-width: 0 2px 0 2px;
      border-color: rgba(28,28,28,0);
      border-style: solid;
      border-radius: ${({theme}) => theme.radius.sm};
    }
    ::-webkit-scrollbar-track {
      margin: 0 0 0 0;
    }
    ::-webkit-scrollbar-button{
      display: none;
    }
  }
`

function App() {
  const [t] = useTranslation();
  const {
    themeDark,
    viewAlert,
    viewModalComplete,
    setViewModalComplete,
    setActiveAnimationComplete,
    activeAnimationComplete,
  } = useUi()
  const activeTheme = useMemo(() => theme(themeDark), [themeDark])
  const navigate = useNavigate()
  const [appLoading, setAppLoading] = useState<boolean>(true)
  const [goNext, setGoNext] = useState<boolean>(false)
  const {getDataToLs, setDataToLs} = useLocalStorage()
  const {
    setUser,
    user,
    appMenu,
    setAppMenu,
    techWork,
    setTechWork,
    setNetworkErr,
    panelNotifications,
  } = useAuth()
  const {
    setAppMenuFeatures,
    setPermissionStatus,
  } = usePermission()
  const previousActiveClientRef = React.useRef<string | number | null | undefined>(undefined)
  const appMenuRequestIdRef = React.useRef(0)
  useEffect(() => {
    document.documentElement.classList.toggle('dark', themeDark)
    document.documentElement.classList.toggle('light', !themeDark)
  }, [themeDark])

  const handleMissingBackendUser = (responseData?: any) => {
    if (isAuthDebugEnabled()) {
      const keycloakInstance = keycloak as any

      console.error('[auth-debug] current-user returned no user; skipped keycloak.logout()', {
        currentUserResponse: responseData,
        host: window.location.hostname,
        apiUrl: config.apiUrl,
        region: localStorage.getItem('region_ocp_zone'),
        keycloak: {
          url: keycloakInstance.authServerUrl,
          realm: keycloakInstance.realm,
          clientId: keycloakInstance.clientId,
          authenticated: keycloakInstance.authenticated,
        },
        token: {
          issuer: keycloakInstance.tokenParsed?.iss,
          audience: keycloakInstance.tokenParsed?.aud,
          authorizedParty: keycloakInstance.tokenParsed?.azp,
          subject: keycloakInstance.tokenParsed?.sub,
        },
      })
      setAppLoading(false)
      setGoNext(true)
      return
    }

    keycloak.logout()
  }
  // const [ref, setRef] = useQueryParam('ref', StringParam);
  // const [cookies, setCookie, removeCookie] = useCookies(['refCode']);
  // console.log('cookies', cookies);
  // useEffect(() => {
  //   if(ref){
  //     setCookie('refCode', ref)
  //   }
  // },[ref])
  // useEffect(() => {
  //   if(cookies?.refCode){
  //     console.log('pidor', cookies);
  //   }
  // },[cookies])
  const getAppMenu = async (reason: string = 'initial load') => {
    const requestId = ++appMenuRequestIdRef.current

    setPermissionStatus('loading', `Loading permission menu for ${reason}`)
    setAppLoading(true)

    try {
      const res = await authService.getAppMenu()

      if (requestId !== appMenuRequestIdRef.current) {
        return
      }

      const features = permissionService.normalizeAppMenuFeatures(res.data)
      setAppMenu(features)
      setAppMenuFeatures(features, `Loaded permission menu for ${reason}`)
    } catch (err:any) {
      if (requestId !== appMenuRequestIdRef.current) {
        return
      }

      setAppMenu([])
      setAppMenuFeatures([], `Cleared permission menu after failure during ${reason}`)
      setPermissionStatus('error', `Failed to load permission menu for ${reason}`)

      if (err?.response?.data?.detail) {
        viewAlert({
          severity:'error',
          message:err.response.data.detail,
        })
      }
    } finally {
      if (requestId !== appMenuRequestIdRef.current) {
        return
      }

      setGoNext(true)
      setAppLoading(false)
    }
  }

  useEffect(() => {
    const activeClientId = user?.user?.active_client ?? null

    if (previousActiveClientRef.current === undefined) {
      previousActiveClientRef.current = activeClientId
      return
    }

    if (previousActiveClientRef.current !== activeClientId) {
      previousActiveClientRef.current = activeClientId
      setAppMenu([])

      if (user?.user) {
        void getAppMenu(`active client ${String(activeClientId ?? 'none')}`)
      }
    }
  }, [user?.user?.active_client])
    
  const handleInvitationAccept = () => {
    const inviteToken = document.cookie.split('; ').find(row => row.startsWith('invite_token='))?.split('=')[1]
    const inviteRegion = document.cookie.split('; ').find(row => row.startsWith('invite_region='))?.split('=')[1]
    const region = localStorage.getItem('region_ocp_zone')
    if(inviteToken && isCurrentInviteRegion(inviteRegion, region)){
      clientService.acceptInvitation({token: inviteToken})
      .then(() => {
        removeCookie('invite_token')
        removeCookie('invite_region')
        authService.getUser()
        .then((res) => {
          if(res.data.user){
            setUser(res.data)
            navigate('/home')
          }
        })
        .catch((e) => {
          if(e?.response?.data?.detail){
            viewAlert({
              severity:'error',
              message:e?.response?.data?.detail,
            })
          }
        })
      })
      .catch(() => {
        viewAlert({
          severity:'error',
          message:`${t("ERROR_OCCURRED")}`
        })
      })
    }
  }
  const updateData = async () => {
    authService.getUser()
    .then((res) => {
      if(!res.data.user){
        handleMissingBackendUser(res.data)
      }
      if(res.data.user){
        getAppMenu()
        setUser(res.data)
        if(res.data.user.active_client){
          if(localStorage.getItem('lang') !== res.data.user.language){
            localStorage.setItem('lang', res.data.user.language)
            window.location.reload()
          }
          // Handle invitation acceptance after user is authenticated
          handleInvitationAccept()
        }
        if(!res.data.user.active_client){
          // Try to accept invitation even without active client
          const inviteToken = document.cookie.split('; ').find(row => row.startsWith('invite_token='))?.split('=')[1]
          if(inviteToken){
            handleInvitationAccept()
          } else {
            navigate('/complete-registration')
          }
        }

      } else {
        setAppLoading(false)
        setGoNext(true)
      }

    })
    .catch((e) => {
      if(e.code === "ERR_NETWORK"){
        setTechWork(true)
        setNetworkErr(true)
        const interval = setInterval(() => {
          authService.getUser()
          .then((res) => {
            clearInterval(interval)
            setTechWork(false)
            setNetworkErr(false)
            if(!res.data.user){
              handleMissingBackendUser(res.data)
            }
            if(res.data.user){
              setUser(res.data)
              if(res.data.user.active_client){
                if(localStorage.getItem('lang') !== res.data.user.language){
                  localStorage.setItem('lang', res.data.user.language)
                  window.location.reload()
                }
              }
              if(!res.data.user.active_client){
                navigate('/complete-registration')
              }
              setGoNext(true)
              setAppLoading(false)
            } else {
              setAppLoading(false)
              setGoNext(true)
            }
          })
          .catch((err:any) => {
            
          })
        },3000)
      }
      if(e?.response?.data?.detail){
        viewAlert({
          severity:'error',
          message:e?.response?.data?.detail,
        })
      }
      // keycloak.logout()
      // setTechWork(true)
      setAppLoading(false)
      setGoNext(true)
    })
  }
  useEffect(() => {
    void updateData()
  },[])
  const [ref, setRef] = useQueryParam('ref', StringParam);
  const [admitad_uid, setAdmitad_uid] = useQueryParam('admitad_uid', StringParam);
  const [utm_source, setUtm_source] = useQueryParam('utm_source', StringParam);
  const [invite_region, setinvite_region] = useQueryParam('invite_region', StringParam);
  const [invite_token, setInvite_token] = useQueryParam('invite_token', StringParam);
  const [invitationToken] = useQueryParam('token', StringParam);
  const [invitationRegion] = useQueryParam('region', StringParam);
  const [cookies, setCookie, removeCookie] = useCookies(['refCode', 'invite_token','invite_region', 'admitad_uid', 'utm_source']);
  useEffect(() => {
    if(ref){
      setCookie('refCode', ref)
    }
  },[ref])
  useEffect(() => {
    if(admitad_uid){
      setCookie('admitad_uid', admitad_uid)
    }
  },[admitad_uid])
  useEffect(() => {
    if(utm_source){
      setCookie('utm_source', utm_source)
    }
  },[utm_source])
  useEffect(() => {
    if(invite_token){
      setCookie('invite_token', invite_token)
    }
    if(invite_region){
      persistInvitationRegion(invite_region, setCookie)
    }
  },[invite_token, invite_region, setCookie])
  // Handle new invitation URL format: /invitation?token={token}&region={region}
  useEffect(() => {
    const isInvitationPath = window.location.pathname.includes('/invitation')
    if(isInvitationPath && invitationToken){
      setCookie('invite_token', invitationToken)
    }
    if(isInvitationPath && invitationRegion){
      persistInvitationRegion(invitationRegion, setCookie)
    }
  },[invitationToken, invitationRegion, setCookie])
  return (
      <>
        <TopProgress loading={appLoading}/>
        <title>{t("PERSONAL_AREA_TITLE")}</title>
        <RouterContextProvider>
          <HeroUIProvider>
            <ThemeProvider theme={activeTheme}>
              <GlobalStyle theme={activeTheme}/>
              <TopPanelNotifications notifications={panelNotifications}/>
              {goNext ? 
                <RootRouter isAuth={false}/>
              : null}
            </ThemeProvider>
          </HeroUIProvider>
        </RouterContextProvider>
      </>
  );
}

export default App;
